import ApiService from "./api.service";
import JwtService from "./jwt.service";

export const mesesIndicadores = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
// Soma a quantidade das tabelas por dimensão, sem somar totais de médico ou percentuais.
export function somarMarcacoesSemMedico(linhas, campoId, campoNome = "nome") {
  const grupos = new Map();
  for(const r of linhas) {
    // Respostas anteriores trazem a dimensão apenas na chave [médico, dimensão].
    const chave = Object.prototype.hasOwnProperty.call(r,campoId) ? r[campoId] : JSON.parse(r.id)[1];
    const id = JSON.stringify(chave == null ? null : String(chave));
    if(!grupos.has(id)) { grupos.set(id,{id,nome:r[campoNome],valor:0}); }
    grupos.get(id).valor += Number(r.quantidade);
  }
  return [...grupos.values()].sort((a,b)=>b.valor-a.valor || a.nome.localeCompare(b.nome,"pt-BR") || a.id.localeCompare(b.id,"pt-BR",{numeric:true}));
}
export const totalIndicador = (rows, campo) => rows.some(r => r[campo] == null || !Number.isFinite(Number(r[campo]))) ? null : rows.reduce((s,r) => s + Number(r[campo]), 0);
export const formatarIndicador = (value, tipo = "numero") => value == null || !Number.isFinite(Number(value)) ? "—" :
  new Intl.NumberFormat("pt-BR", tipo === "moeda" ? { style: "currency", currency: "BRL" } : { maximumFractionDigits: 2 }).format(Number(value)) + (tipo === "percentual" ? "%" : tipo === "minutos" ? " min" : "");

export async function consultarIndicadores(acao, dados = {}, signal) {
  if(!JwtService.getToken()) { throw new Error("Sessão indisponível. Entre novamente no sistema."); }
  ApiService.setHeader();
  try {
    const { data } = await ApiService.postOnce("indicadoresAnalytics/access/" + acao, dados, { signal, timeout: 120000 });
    if(data?.contrato !== "INDICADORES_ANALYTICS_V1" || data.resultado !== true || data.dados == null) { throw new Error(data?.mensagem || "Resposta inválida ao consultar indicadores."); }
    return data.dados;
  } catch(error) {
    if(signal?.aborted) { throw new Error("Consulta cancelada."); }
    throw new Error(error.response?.data?.mensagem || error.message || "Não foi possível atualizar os indicadores.");
  }
}

export function opcaoGraficoIndicador(rows, { tipo = "bar", moeda = false, horizontal = false } = {}) {
  const fmt = value => formatarIndicador(value, moeda ? "moeda" : "numero");
  const base = { animationDuration: 250, color: ["#168c9c","#4b7bec","#2db98d","#f4b64b","#ab78d1","#ed7a6c"],
    aria: { enabled: true }, tooltip: { trigger: tipo === "pie" ? "item" : "axis", renderMode: "richText", valueFormatter: fmt },
    textStyle: { fontFamily: "Inter, Arial, sans-serif", fontSize: 11 } };
  if(tipo === "pie") { return { ...base, legend: { type: "scroll", bottom: 0 }, series: [{ type: "pie", radius: ["38%","65%"], center: ["50%","43%"], label: { formatter: "{d}%" }, data: rows.map(r => ({ name: r.nome, value: r.valor })) }] }; }
  const categorias = { type: "category", data: rows.map(r => r.nome), axisLabel: { width: horizontal ? 150 : 85, overflow: "truncate", rotate: horizontal ? 0 : (rows.length > 8 ? 35 : 0) } };
  const valores = { type: "value", axisLabel: { formatter: value => new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(value) } };
  return { ...base, grid: { left: horizontal ? 160 : 55, right: 24, top: 24, bottom: rows.length > 12 ? 68 : 48, containLabel: false },
    xAxis: horizontal ? valores : categorias, yAxis: horizontal ? { ...categorias, inverse: true } : valores,
    dataZoom: rows.length > 12 ? [{ type: "slider", [horizontal ? "yAxisIndex" : "xAxisIndex"]: 0, start: 0, end: Math.min(100,1200/rows.length), height: horizontal ? undefined : 14 }] : [],
    series: [{ type: tipo, data: rows.map(r => r.valor), barMaxWidth: 38, smooth: false, emphasis: { focus: "series" }, areaStyle: tipo === "line" ? { opacity: 0.08 } : undefined }] };
}

export function opcaoPerformance(rows) {
  return { color: ["#2db98d","#ed7a6c","#f4b64b"], aria: { enabled: true }, tooltip: { trigger: "axis", renderMode: "richText", valueFormatter: value => formatarIndicador(value,"moeda") },
    legend: { top: 0 }, grid: { left: 72, right: 22, top: 42, bottom: 35 }, xAxis: { type: "category", data: rows.map(r => mesesIndicadores[r.mes-1].slice(0,3)) },
    yAxis: { type: "value", axisLabel: { formatter: value => new Intl.NumberFormat("pt-BR", { notation:"compact" }).format(value) } },
    series: [{ name:"Entradas", type:"bar", data:rows.map(r=>r.entradas) }, { name:"Despesas", type:"bar", data:rows.map(r=>r.despesas) },
      { name:"Meta", type:"line", connectNulls:false, lineStyle:{type:"dashed"}, data:rows.map(r=>r.meta) }] };
}

export function csvIndicadores(colunas, rows) {
  const celula = v => '"' + String(v ?? "").replace(/^[=+@-]/,"'$&").replace(/"/g,'""') + '"';
  return "\uFEFF" + [colunas.map(c=>celula(c.nome)).join(";"), ...rows.map(r=>colunas.map(c=>celula(c.tipo ? formatarIndicador(r[c.campo],c.tipo) : r[c.campo])).join(";"))].join("\r\n");
}

// Apenas a solicitação mais recente pode atualizar os números exibidos.
export function carregadorIndicadores(request = consultarIndicadores) {
  let versao = 0, controller;
  return {
    cancelar() { versao++; controller?.abort(); },
    async carregar(acao, filtros, aplicar, falhar, terminar) {
      controller?.abort(); controller = new AbortController(); const atual = ++versao;
      try { const dados = await request(acao, JSON.parse(JSON.stringify(filtros)), controller.signal); if(atual === versao) { aplicar(dados); } }
      catch(error) { if(atual === versao) { falhar(error.message); } }
      finally { if(atual === versao) { terminar(); } }
    }
  };
}
